import * as t from "io-ts";
import { positiveIntCodec } from "./common";
import { messages } from "../utils/i18n/i18n";
import {
    array,
    monoid,
    nonEmptyArray,
    number,
    ord,
    record,
    tuple,
} from "fp-ts";
import { pipe } from "fp-ts/function";
import { contramap } from "fp-ts/Ord";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { assertUnreachable } from "../utils/assertUnreachable";

export const clubScoringMethodLabels = messages(
    {
        timeSum: "Sum of times",
        rankSum: "Sum of ranks",
    },
    {
        cs: {
            timeSum: "Součet časů",
            rankSum: "Součet umístění",
        },
    }
);
export const clubScoringMethodCodec = t.keyof(clubScoringMethodLabels.en);
export type ClubScoringMethod = t.TypeOf<typeof clubScoringMethodCodec>;

export const clubScoringCodec = t.type(
    {
        scoringMembersCount: positiveIntCodec,
        scoringMethod: clubScoringMethodCodec,
    },
    "ClubScoring"
);

export type ClubScoring = t.TypeOf<typeof clubScoringCodec>;

export interface ClubScoringMember {
    clubTitle: string;
    rank: number;
    time: number;
}

export interface ClubStanding<M extends ClubScoringMember> {
    club: string;
    rank: number;
    // time sum or rank sum, depending on the scoring method
    sum: number;
    // all ranked club members, not only the scoring ones
    members: NonEmptyArray<M>;
}

const memberOrdByRank = pipe(
    number.Ord,
    contramap((member: ClubScoringMember) => member.rank)
);

const ordByFinishersCountDesc = (scoringMembersCount: number) =>
    pipe(
        number.Ord,
        contramap((s: ClubStanding<ClubScoringMember>) =>
            Math.min(s.members.length, scoringMembersCount)
        ),
        ord.reverse
    );

const ordBySumAsc = pipe(
    number.Ord,
    contramap((s: ClubStanding<ClubScoringMember>) => s.sum)
);

const clubStandingOrd = (clubScoring: ClubScoring) =>
    monoid.concatAll(ord.getMonoid<ClubStanding<ClubScoringMember>>())([
        ordByFinishersCountDesc(clubScoring.scoringMembersCount),
        ordBySumAsc,
    ]);

const memberScore = (clubScoring: ClubScoring) => {
    switch (clubScoring.scoringMethod) {
        case "timeSum":
            return (member: ClubScoringMember) => member.time;

        case "rankSum":
            return (member: ClubScoringMember) => member.rank;
    }

    assertUnreachable(clubScoring.scoringMethod);
};

/**
 * clubs ordered by the count of scoring members in the finish (desc),
 * then by the sum of the best `scoringMembersCount` members (asc)
 */
export const computeClubStandings =
    (clubScoring: ClubScoring) =>
    <M extends ClubScoringMember>(members: Array<M>): Array<ClubStanding<M>> =>
        pipe(
            members,
            nonEmptyArray.groupBy((member) => member.clubTitle),
            record.mapWithIndex(
                (club, clubMembers): ClubStanding<M> => ({
                    club,
                    members: clubMembers,
                    sum: pipe(
                        clubMembers,
                        array.sort(memberOrdByRank),
                        array.takeLeft(clubScoring.scoringMembersCount),
                        array.map(memberScore(clubScoring)),
                        array.reduce(0, (acc, score) => acc + score)
                    ),
                    rank: 0, // assigned after sorting
                })
            ),
            record.toArray,
            array.map(tuple.snd),
            array.sort(clubStandingOrd(clubScoring)),
            array.mapWithIndex((i, standing) => ({
                ...standing,
                rank: i + 1,
            }))
        );
